import { isDromRegion, RegionList, Regions } from '../referential/Region';
import {
  type Matrix,
  type MatrixKind,
  matrixKindLabel
} from '../schema/Matrix/MatrixReferential';
import {
  type ProgrammingPlanDomain,
  ProgrammingPlanDomainId
} from '../schema/ProgrammingPlan/ProgrammingPlanDomain';
import { defaultProgrammingPlanSample } from '../schema/ProgrammingPlan/ProgrammingPlanSampleSetting';
import { ProgrammingPlanStatusList } from '../schema/ProgrammingPlan/ProgrammingPlanStatus';
import type { ProgrammingPlanChecked } from '../schema/ProgrammingPlan/ProgrammingPlans';
import {
  type ProgrammingSubPlan,
  ProgrammingSubPlanId,
  type SachaContextSigle
} from '../schema/ProgrammingPlan/ProgrammingSubPlan';
import type { SubPlanMatrices } from '../schema/ProgrammingPlan/SubPlanMatrices';
import { MatrixReferentialFixture } from './matrixFixtures';
import { oneOf } from './testFixtures';

const currentYear = new Date().getFullYear();

export const PesticideResidueDomainId = ProgrammingPlanDomainId.parse(
  '09a95048-64fe-46a1-8543-50146c6ab337'
);
const ChemicalContaminantDomainId = ProgrammingPlanDomainId.parse(
  'be1fb96c-e498-4e7a-bd2b-cd3d808f997f'
);
const PesticideResiduePreviousYearDomainId = ProgrammingPlanDomainId.parse(
  '1a2b3c4d-5e6f-4a7b-8c9d-0e1f2a3b4c5d'
);
const PesticideResidueNextYearDomainId = ProgrammingPlanDomainId.parse(
  '2b3c4d5e-6f7a-4b8c-9d0e-1f2a3b4c5d6e'
);
const PesticideResidueInTwoYearsDomainId = ProgrammingPlanDomainId.parse(
  '3c4d5e6f-7a8b-4c9d-8e0f-2a3b4c5d6e7f'
);
const PesticideResidueInTenYearsDomainId = ProgrammingPlanDomainId.parse(
  '4d5e6f7a-8b9c-4d0e-9f1a-3b4c5d6e7f8a'
);

export const genProgrammingPlanDomain = (
  data?: Partial<ProgrammingPlanDomain>
): ProgrammingPlanDomain => ({
  id: ProgrammingPlanDomainId.parse(crypto.randomUUID()),
  label: 'Résidus de pesticides',
  year: currentYear,
  ...data
});

export const ProgrammingPlanDomainFixtures: ProgrammingPlanDomain[] = [
  genProgrammingPlanDomain({ id: PesticideResidueDomainId }),
  genProgrammingPlanDomain({
    id: ChemicalContaminantDomainId,
    label: 'Contaminants chimiques'
  }),
  genProgrammingPlanDomain({
    id: PesticideResiduePreviousYearDomainId,
    year: currentYear - 1
  }),
  genProgrammingPlanDomain({
    id: PesticideResidueNextYearDomainId,
    year: currentYear + 1
  }),
  genProgrammingPlanDomain({
    id: PesticideResidueInTwoYearsDomainId,
    year: currentYear + 2
  }),
  genProgrammingPlanDomain({
    id: PesticideResidueInTenYearsDomainId,
    year: currentYear + 10
  })
];

export const NationalCoordinatorId = '55555555-5555-5555-5555-555555555555';
export const NationalCoordinatorName = 'Nadia Coordination';
export const NationalCoordinatorEmail = 'nadia.coordination@example.net';

export const NationalCoordinatorDaoaId = '14141414-1414-1414-1414-141414141414';
export const NationalCoordinatorDaoaName = 'Damien Coordination';
export const NationalCoordinatorDaoaEmail = 'damien.coordination@example.net';

const DaoaNationalCoordinators = [
  {
    id: NationalCoordinatorDaoaId,
    name: NationalCoordinatorDaoaName,
    email: NationalCoordinatorDaoaEmail
  }
];

export const NationalCoordinatorAlanId = '15151515-1515-1515-1515-151515151515';
const NationalCoordinatorAlanName = 'Alice Coordination';
const NationalCoordinatorAlanEmail = 'alice.coordination@example.net';

const AlanNationalCoordinators = [
  {
    id: NationalCoordinatorAlanId,
    name: NationalCoordinatorAlanName,
    email: NationalCoordinatorAlanEmail
  }
];

const PPVClosedProgrammingPlanId = 'f5d510ef-ab78-449a-acd6-392895a1994f';
const PPVValidatedProgrammingPlanId = 'd78fb3eb-1998-482b-9014-282d51ae30b8';
const PPVValidatedDromProgrammingPlanId =
  'c9e8a1b2-9c3d-4f0e-9c5a-1a2b3c4d5e6f';
const PPVInProgressProgrammingPlanId = 'bac693a5-9475-4e24-a775-5532b0117e5b';
const PPVSubmittedProgrammingPlanId = 'e7c8a1b2-9c3d-4f0e-9c5a-1a2b3c4d5e6f';
const DAOAValidatedProgrammingPlanId = 'd2680960-a3b5-4091-a87b-e4c2467077fb';
const DAOAInProgressProgrammingPlanId = 'fafc6f2e-aec5-4998-adeb-84090d971a90';
const ALANProgrammingPlanId = 'a4f0c2d1-8b7e-4c3a-9d5f-6e1b2c3d4a5b';

export const PPVValidatedSubPlanId = ProgrammingSubPlanId.parse(
  'a1a1a1a1-a1a1-a1a1-a1a1-a1a1a1a1a1a1'
);
const PPVValidatedDromSubPlanId = ProgrammingSubPlanId.parse(
  'a1a1a1a1-a1a1-a1a1-a1a1-a1a1a1a1a1a5'
);
const PPVClosedSubPlanId = ProgrammingSubPlanId.parse(
  'a1a1a1a1-a1a1-a1a1-a1a1-a1a1a1a1a1a2'
);
export const PPVInProgressSubPlanId = ProgrammingSubPlanId.parse(
  'a1a1a1a1-a1a1-a1a1-a1a1-a1a1a1a1a1a3'
);
const PPVSubmittedSubPlanId = ProgrammingSubPlanId.parse(
  'a1a1a1a1-a1a1-a1a1-a1a1-a1a1a1a1a1a4'
);
export const DAOAVolailleValidatedSubPlanId = ProgrammingSubPlanId.parse(
  'b2b2b2b2-b2b2-b2b2-b2b2-b2b2b2b2b2b2'
);
export const DAOABovinValidatedSubPlanId = ProgrammingSubPlanId.parse(
  'c3c3c3c3-c3c3-c3c3-c3c3-c3c3c3c3c3c3'
);
export const DAOAInProgressVolailleSubPlanId = ProgrammingSubPlanId.parse(
  'b2b2b2b2-b2b2-b2b2-b2b2-b2b2b2b2b2b4'
);
export const DAOAInProgressBovinSubPlanId = ProgrammingSubPlanId.parse(
  'c3c3c3c3-c3c3-c3c3-c3c3-c3c3c3c3c3c4'
);
export const ALANRuminantsSubPlanId = ProgrammingSubPlanId.parse(
  'd4d4d4d4-d4d4-d4d4-d4d4-d4d4d4d4d4d4'
);
export const ALANVolaillesSubPlanId = ProgrammingSubPlanId.parse(
  'd4d4d4d4-d4d4-d4d4-d4d4-d4d4d4d4d4d5'
);

const SachaSubPlanIds = [
  DAOAVolailleValidatedSubPlanId,
  DAOABovinValidatedSubPlanId,
  DAOAInProgressVolailleSubPlanId,
  DAOAInProgressBovinSubPlanId
];

export const genSubPlanMatrices = (
  matrixKind: MatrixKind,
  matrices: Matrix[] = []
): SubPlanMatrices => ({
  operator: 'Or',
  items: [{ matrixKind, matrices }]
});

export const genProgrammingSubPlan = (
  data?: Partial<ProgrammingSubPlan>
): ProgrammingSubPlan => ({
  id: ProgrammingSubPlanId.parse(crypto.randomUUID()),
  programmingPlanId: crypto.randomUUID(),
  subPlanNumber: 'TEST',
  stages: [],
  stagesManaged: true,
  settingsCompleted: true,
  withSacha: (data?.id && SachaSubPlanIds.includes(data.id)) ?? false,
  sachaContextSigle: null,
  substanceKinds: ['Mono', 'Multi'],
  substanceKindsManaged: true,
  samples: (data?.substanceKinds ?? ['Mono', 'Multi']).map((substanceKind) => ({
    ...defaultProgrammingPlanSample,
    substanceKinds: [substanceKind]
  })),
  samplesManaged: true,
  matrices: null,
  matricesManaged: true,
  context: 'Control',
  contextManaged: true,
  programmingInstruction: null,
  programmingInstructionManaged: true,
  notes: null,
  notesManaged: true,
  monoSubstances: null,
  monoSubstancesManaged: true,
  multiSubstances: null,
  multiSubstancesManaged: true,
  sampleProcedure: null,
  sampleProcedureManaged: true,
  delegated: false,
  delegatedManaged: true,
  ...data
});

export const PPVValidatedSubPlanFixture = genProgrammingSubPlan({
  id: PPVValidatedSubPlanId,
  programmingPlanId: PPVValidatedProgrammingPlanId,
  subPlanNumber: 'PPV01',
  stages: [
    { stage: 'PRODUCTION_PRIMAIRE_VEGETALE', subStages: [] },
    { stage: 'ALIMENTATION_ANIMALE', subStages: [] },
    { stage: 'TRANSFORMATION', subStages: [] }
  ],
  contactListId: 7,
  withSacha: false,
  substanceKinds: ['Mono', 'Multi'],
  samples: [
    { ...defaultProgrammingPlanSample, substanceKinds: ['Mono', 'Multi'] }
  ],
  matrices: genSubPlanMatrices('A00GY' as MatrixKind),
  monoSubstances: ['RF-1020-001-PPP']
});
export const PPVValidatedDromSubPlanFixture = genProgrammingSubPlan({
  ...PPVValidatedSubPlanFixture,
  id: PPVValidatedDromSubPlanId,
  programmingPlanId: PPVValidatedDromProgrammingPlanId,
  context: 'Surveillance'
});

export const PPVClosedSubPlanFixture = genProgrammingSubPlan({
  ...PPVValidatedSubPlanFixture,
  id: PPVClosedSubPlanId,
  programmingPlanId: PPVClosedProgrammingPlanId
});

export const PPVInProgressSubPlanFixture = genProgrammingSubPlan({
  ...PPVValidatedSubPlanFixture,
  id: PPVInProgressSubPlanId,
  programmingPlanId: PPVInProgressProgrammingPlanId
});

export const PPVSubmittedSubPlanFixture = genProgrammingSubPlan({
  ...PPVValidatedSubPlanFixture,
  id: PPVSubmittedSubPlanId,
  programmingPlanId: PPVSubmittedProgrammingPlanId
});

const DAOASampleProcedure = {
  unitCount: null,
  minQuantity: '200 grammes',
  maxQuantity: null,
  container: 'Contenant en plastique',
  samplingTemperature: null,
  storageTemperature: '-18°',
  maxAnalysisDelay: '30 jours'
};

export const DAOAVolailleValidatedSubPlanFixture = genProgrammingSubPlan({
  id: DAOAVolailleValidatedSubPlanId,
  programmingPlanId: DAOAValidatedProgrammingPlanId,
  subPlanNumber: 'M01',
  stages: [{ stage: 'ABATTAGE', subStages: [] }],
  contactListId: 9,
  withSacha: true,
  sachaContextSigle: 'PR17_2026_RPDA_PVOL' as SachaContextSigle,
  substanceKinds: ['Mono', 'Multi', 'Copper'],
  matrices: genSubPlanMatrices('A01SN' as MatrixKind),
  sampleProcedure: DAOASampleProcedure,
  context: 'Surveillance'
});

export const DAOAVolailleInProgressSubPlanFixture = genProgrammingSubPlan({
  ...DAOAVolailleValidatedSubPlanFixture,
  id: DAOAInProgressVolailleSubPlanId,
  programmingPlanId: DAOAInProgressProgrammingPlanId,
  settingsCompleted: false
});

export const DAOABovinValidatedSubPlanFixture = genProgrammingSubPlan({
  id: DAOABovinValidatedSubPlanId,
  programmingPlanId: DAOAValidatedProgrammingPlanId,
  subPlanNumber: 'M02',
  stages: [{ stage: 'ABATTAGE', subStages: [] }],
  contactListId: 9,
  withSacha: true,
  sachaContextSigle: 'PR17_2026_RPDA_PBOV' as SachaContextSigle,
  substanceKinds: ['Mono', 'Multi', 'Copper'],
  matrices: genSubPlanMatrices('A01QX' as MatrixKind, [
    'A01XF#F28.A0C0S' as Matrix
  ]),
  sampleProcedure: DAOASampleProcedure,
  context: 'Surveillance'
});

export const DAOABovinInProgressSubPlanFixture = genProgrammingSubPlan({
  ...DAOABovinValidatedSubPlanFixture,
  id: DAOAInProgressBovinSubPlanId,
  programmingPlanId: DAOAInProgressProgrammingPlanId,
  settingsCompleted: false,
  programmingInstruction: 'Instructions pour le foie de bovin',
  notes: 'Prescription pour le foie de bovin'
});

export const ALANRuminantsSubPlanFixture = genProgrammingSubPlan({
  id: ALANRuminantsSubPlanId,
  programmingPlanId: ALANProgrammingPlanId,
  subPlanNumber: 'ALAN01',
  stages: [{ stage: 'ALIMENTATION_ANIMALE', subStages: [] }],
  substanceKinds: ['Mono', 'Multi'],
  matrices: genSubPlanMatrices('TODO_16' as MatrixKind),
  context: 'Surveillance'
});

export const ALANVolaillesSubPlanFixture = genProgrammingSubPlan({
  id: ALANVolaillesSubPlanId,
  programmingPlanId: ALANProgrammingPlanId,
  subPlanNumber: 'ALAN02',
  stages: [{ stage: 'ALIMENTATION_ANIMALE', subStages: [] }],
  substanceKinds: ['Mono', 'Multi'],
  matrices: genSubPlanMatrices('TODO_18' as MatrixKind),
  context: 'Surveillance'
});

export const genProgrammingPlan = (
  data?: Partial<ProgrammingPlanChecked>
): ProgrammingPlanChecked => {
  const planId = data?.id ?? crypto.randomUUID();
  return {
    id: planId,
    domainId: PesticideResidueDomainId,
    title: 'Production primaire végétale',
    subPlans: [
      {
        ...PPVValidatedSubPlanFixture,
        id: ProgrammingSubPlanId.parse(crypto.randomUUID()),
        programmingPlanId: planId
      }
    ],
    distributionKind: 'REGIONAL',
    nationalCoordinators: [
      {
        id: NationalCoordinatorId,
        name: NationalCoordinatorName,
        email: NationalCoordinatorEmail
      }
    ],
    stages: null,
    stagesManaged: false,
    substanceKinds: null,
    substanceKindsManaged: false,
    samples: null,
    samplesManaged: false,
    matrices: null,
    matricesManaged: false,
    context: null,
    contextManaged: false,
    programmingInstruction: null,
    programmingInstructionManaged: false,
    notes: null,
    notesManaged: false,
    monoSubstances: null,
    monoSubstancesManaged: false,
    multiSubstances: null,
    multiSubstancesManaged: false,
    sampleProcedure: null,
    sampleProcedureManaged: false,
    delegated: null,
    delegatedManaged: false,
    settingsCompleted: true,
    createdAt: new Date(),
    createdBy: crypto.randomUUID(),
    launchedAt: new Date(),
    launchedBy: NationalCoordinatorId,
    nationalStatus: { status: oneOf(ProgrammingPlanStatusList) },
    regionalStatus: RegionList.map((region) => ({
      region,
      status: oneOf(ProgrammingPlanStatusList)
    })),
    departmentalStatus: [],
    year: new Date().getFullYear(),
    closedAt: null,
    closedBy: null,
    ...data
  };
};

export const withMatrixKindSubPlans = <T extends ProgrammingPlanChecked>(
  programmingPlan: T,
  matrixKinds: MatrixKind[]
): T => ({
  ...programmingPlan,
  subPlans: matrixKinds.map((matrixKind, index) => ({
    ...programmingPlan.subPlans[0],
    id:
      index === 0
        ? programmingPlan.subPlans[0].id
        : ProgrammingSubPlanId.parse(crypto.randomUUID()),
    subPlanNumber: `PPV${String(index + 1).padStart(2, '0')}`,
    label: matrixKindLabel(MatrixReferentialFixture, matrixKind),
    matrices: genSubPlanMatrices(matrixKind)
  }))
});

export const genDeletableProgrammingPlan = (
  data?: Partial<ProgrammingPlanChecked>
): ProgrammingPlanChecked => {
  const planId = data?.id ?? crypto.randomUUID();
  return genProgrammingPlan({
    settingsCompleted: false,
    createdBy: NationalCoordinatorId,
    subPlans: [
      genProgrammingSubPlan({
        programmingPlanId: planId,
        settingsCompleted: false
      })
    ],
    ...data,
    id: planId
  });
};

export const PPVClosedProgrammingPlanFixture = genProgrammingPlan({
  stages: [
    { stage: 'PRODUCTION_PRIMAIRE_VEGETALE', subStages: [] },
    { stage: 'ALIMENTATION_ANIMALE', subStages: [] },
    { stage: 'TRANSFORMATION', subStages: [] }
  ],
  stagesManaged: true,
  id: PPVClosedProgrammingPlanId,
  domainId: PesticideResiduePreviousYearDomainId,
  title: 'Production primaire végétale',
  subPlans: [PPVClosedSubPlanFixture],
  distributionKind: 'REGIONAL',
  createdAt: new Date(),
  createdBy: NationalCoordinatorId,
  closedAt: new Date(),
  closedBy: NationalCoordinatorId,
  nationalStatus: { status: 'Closed' },
  regionalStatus: RegionList.map((region) => ({
    region,
    status: 'Closed'
  })),
  year: new Date().getFullYear() - 1
});

export const PPVValidatedProgrammingPlanFixture = genProgrammingPlan({
  stages: [
    { stage: 'PRODUCTION_PRIMAIRE_VEGETALE', subStages: [] },
    { stage: 'ALIMENTATION_ANIMALE', subStages: [] },
    { stage: 'TRANSFORMATION', subStages: [] }
  ],
  stagesManaged: true,
  id: PPVValidatedProgrammingPlanId,
  domainId: PesticideResidueDomainId,
  title: 'Production primaire végétale',
  subPlans: [PPVValidatedSubPlanFixture],
  distributionKind: 'REGIONAL',
  createdAt: new Date(),
  createdBy: NationalCoordinatorId,
  nationalStatus: { status: 'SubmittedToRegion' },
  regionalStatus: RegionList.toSorted().map((region) => ({
    region,
    status: 'Validated'
  })),
  year: new Date().getFullYear()
});

export const PPVValidatedDromProgrammingPlanFixture = genProgrammingPlan({
  stages: [
    { stage: 'PRODUCTION_PRIMAIRE_VEGETALE', subStages: [] },
    { stage: 'ALIMENTATION_ANIMALE', subStages: [] },
    { stage: 'TRANSFORMATION', subStages: [] }
  ],
  stagesManaged: true,
  id: PPVValidatedDromProgrammingPlanId,
  domainId: PesticideResidueInTenYearsDomainId,
  title: 'Production primaire végétale - DROM',
  subPlans: [PPVValidatedDromSubPlanFixture],
  distributionKind: 'REGIONAL',
  createdAt: new Date(),
  createdBy: NationalCoordinatorId,
  nationalStatus: { status: 'SubmittedToRegion' },
  regionalStatus: RegionList.map((region) => ({
    region,
    status: isDromRegion(region) ? 'Validated' : 'SubmittedToRegion'
  })),
  year: new Date().getFullYear() + 10
});

export const PPVInProgressProgrammingPlanFixture = genProgrammingPlan({
  stages: [
    { stage: 'PRODUCTION_PRIMAIRE_VEGETALE', subStages: [] },
    { stage: 'ALIMENTATION_ANIMALE', subStages: [] },
    { stage: 'TRANSFORMATION', subStages: [] }
  ],
  stagesManaged: true,
  id: PPVInProgressProgrammingPlanId,
  domainId: PesticideResidueNextYearDomainId,
  title: 'Production primaire végétale',
  subPlans: [PPVInProgressSubPlanFixture],
  distributionKind: 'REGIONAL',
  createdAt: new Date(),
  createdBy: NationalCoordinatorId,
  launchedAt: null,
  launchedBy: null,
  nationalStatus: { status: 'InProgress' },
  regionalStatus: RegionList.map((region) => ({
    region,
    status: 'InProgress'
  })),
  year: new Date().getFullYear() + 1
});

export const PPVSubmittedProgrammingPlanFixture = genProgrammingPlan({
  stages: [
    { stage: 'PRODUCTION_PRIMAIRE_VEGETALE', subStages: [] },
    { stage: 'ALIMENTATION_ANIMALE', subStages: [] },
    { stage: 'TRANSFORMATION', subStages: [] }
  ],
  stagesManaged: true,
  id: PPVSubmittedProgrammingPlanId,
  domainId: PesticideResidueInTwoYearsDomainId,
  title: 'Production primaire végétale',
  subPlans: [PPVSubmittedSubPlanFixture],
  distributionKind: 'REGIONAL',
  createdAt: new Date(),
  createdBy: NationalCoordinatorId,
  nationalStatus: { status: 'SubmittedToRegion' },
  regionalStatus: RegionList.map((region) => ({
    region,
    status: 'SubmittedToRegion'
  })),
  year: new Date().getFullYear() + 2
});

export const DAOAValidatedProgrammingPlanFixture = genProgrammingPlan({
  id: DAOAValidatedProgrammingPlanId,
  nationalCoordinators: DaoaNationalCoordinators,
  domainId: PesticideResidueDomainId,
  title: "Produit carné à l'abattoir",
  subPlans: [
    DAOAVolailleValidatedSubPlanFixture,
    DAOABovinValidatedSubPlanFixture
  ],
  distributionKind: 'SLAUGHTERHOUSE',
  createdAt: new Date(),
  createdBy: NationalCoordinatorId,
  nationalStatus: { status: 'SubmittedToRegion' },
  regionalStatus: RegionList.map((region) => ({
    region,
    status: 'Validated'
  })),
  departmentalStatus: RegionList.flatMap((region) =>
    Regions[region].departments.map((department) => ({
      region,
      department,
      status: 'Validated'
    }))
  ),
  year: new Date().getFullYear()
});

export const ALANProgrammingPlanFixture = genProgrammingPlan({
  id: ALANProgrammingPlanId,
  nationalCoordinators: AlanNationalCoordinators,
  domainId: ChemicalContaminantDomainId,
  title: 'Alimentation animale',
  subPlans: [ALANRuminantsSubPlanFixture, ALANVolaillesSubPlanFixture],
  distributionKind: 'DEPARTMENTAL',
  createdAt: new Date(),
  createdBy: NationalCoordinatorAlanId,
  nationalStatus: { status: 'SubmittedToRegion' },
  regionalStatus: RegionList.map((region) => ({
    region,
    status: 'SubmittedToDepartments'
  })),
  departmentalStatus: RegionList.flatMap((region) =>
    Regions[region].departments.map((department) => ({
      region,
      department,
      status: 'SubmittedToDepartments'
    }))
  ),
  year: new Date().getFullYear()
});

export const DAOAInProgressProgrammingPlanFixture = genProgrammingPlan({
  id: DAOAInProgressProgrammingPlanId,
  nationalCoordinators: DaoaNationalCoordinators,
  domainId: PesticideResidueNextYearDomainId,
  title: "Produit carné à l'abattoir",
  subPlans: [
    DAOAVolailleInProgressSubPlanFixture,
    DAOABovinInProgressSubPlanFixture
  ],
  distributionKind: 'SLAUGHTERHOUSE',
  settingsCompleted: false,
  createdAt: new Date(),
  createdBy: NationalCoordinatorId,
  launchedAt: null,
  launchedBy: null,
  nationalStatus: { status: 'InProgress' },
  regionalStatus: RegionList.map((region) => ({
    region,
    status: 'InProgress'
  })),
  year: new Date().getFullYear() + 1
});
